import type { StateRate } from '@mollusk/shared';
import { stateAbbrToFips, stateName } from '@mollusk/shared';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

/** Tile-grid position [column, row] for each state + DC. */
const TILES: Record<string, [number, number]> = {
  AK: [0, 0],
  ME: [10, 0],
  WI: [5, 1],
  VT: [9, 1],
  NH: [10, 1],
  WA: [0, 2],
  ID: [1, 2],
  MT: [2, 2],
  ND: [3, 2],
  MN: [4, 2],
  IL: [5, 2],
  MI: [6, 2],
  NY: [8, 2],
  MA: [9, 2],
  OR: [0, 3],
  NV: [1, 3],
  WY: [2, 3],
  SD: [3, 3],
  IA: [4, 3],
  IN: [5, 3],
  OH: [6, 3],
  PA: [7, 3],
  NJ: [8, 3],
  CT: [9, 3],
  RI: [10, 3],
  CA: [0, 4],
  UT: [1, 4],
  CO: [2, 4],
  NE: [3, 4],
  MO: [4, 4],
  KY: [5, 4],
  WV: [6, 4],
  VA: [7, 4],
  MD: [8, 4],
  DE: [9, 4],
  AZ: [1, 5],
  NM: [2, 5],
  KS: [3, 5],
  AR: [4, 5],
  TN: [5, 5],
  NC: [6, 5],
  SC: [7, 5],
  DC: [8, 5],
  OK: [3, 6],
  LA: [4, 6],
  MS: [5, 6],
  AL: [6, 6],
  GA: [7, 6],
  HI: [0, 7],
  TX: [3, 7],
  FL: [8, 7],
};

/** Sequential teal ramp, light → dark (magnitude). */
const RAMP = ['#E0F2F1', '#A7D8D5', '#62B6B1', '#22908C', '#0B5F5E'];
/** Thin coverage: a neutral 45° hatch (texture, not a ramp color). */
const HATCH = 'repeating-linear-gradient(45deg, #F2F4F7 0 4px, #D0D5DD 4px 6px)';
const NONE = '#F9FAFB';

const fullName = (st: string) => {
  const f = stateAbbrToFips(st);
  return (f && stateName(f)) ?? st;
};

/** Quantile breaks over the non-thin states that have the tag. */
function breaks(rows: readonly StateRate[]): number[] {
  const v = rows
    .filter((r) => !r.thin && r.per10k > 0)
    .map((r) => r.per10k)
    .sort((a, b) => a - b);
  if (v.length === 0) return [];
  return [0.2, 0.4, 0.6, 0.8].map((q) => v[Math.min(v.length - 1, Math.floor(q * v.length))] ?? 0);
}

/**
 * State tile map (#172): shaded by permits with the tag in the last 12 months
 * per 10,000 residents of the jurisdictions we cover. States where we cover
 * under 40% of people are hatched as thin coverage.
 */
export function StateTileMap({
  rows,
  selected,
  onSelect,
}: {
  rows: readonly StateRate[];
  selected: string | null;
  onSelect: (state: string) => void;
}) {
  const byState = new Map(rows.map((r) => [r.state, r]));
  const cuts = breaks(rows);
  const step = (v: number) => cuts.filter((c) => v >= c).length;
  return (
    <Box>
      <Box
        component="fieldset"
        aria-label="Rate by state"
        sx={{
          border: 0,
          m: 0,
          p: 0,
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(11, 1fr)',
          gap: '4px',
        }}
      >
        {Object.entries(TILES).map(([st, [c, r]]) => {
          const row = byState.get(st);
          const s = row && !row.thin && row.per10k > 0 ? step(row.per10k) : -1;
          const bg = !row ? NONE : row.thin ? HATCH : s < 0 ? NONE : (RAMP[s] ?? NONE);
          const dark = s >= 3;
          const label = !row
            ? `${fullName(st)}: no coverage`
            : row.thin
              ? `${fullName(st)}: thin coverage (we cover ${Math.round(row.share_covered * 100)}% of people)`
              : `${fullName(st)}: ${row.per10k.toFixed(1)} per 10,000 residents covered (we cover ${Math.round(row.share_covered * 100)}% of people)`;
          return (
            <ButtonBase
              key={st}
              data-testid={`tile-${st}`}
              aria-label={label}
              aria-pressed={selected === st}
              title={label}
              onClick={() => onSelect(st)}
              sx={{
                gridColumn: c + 1,
                gridRow: r + 1,
                aspectRatio: '1',
                borderRadius: 1,
                background: bg,
                outline: selected === st ? '2px solid #0F1B2D' : 'none',
                outlineOffset: -2,
                fontSize: { xs: 10, sm: 13 },
                fontWeight: 600,
                color: dark ? '#fff' : '#344054',
              }}
            >
              {st}
            </ButtonBase>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Per 10,000 residents covered:
        </Typography>
        {RAMP.map((c, i) => (
          <Box key={c} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: c }} />
            <Typography variant="caption" color="text.secondary">
              {i === 0 ? `< ${(cuts[0] ?? 0).toFixed(1)}` : `${(cuts[i - 1] ?? 0).toFixed(1)}+`}
            </Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, borderRadius: 0.5, background: HATCH }} />
          <Typography variant="caption" color="text.secondary">
            Thin coverage (we cover under 40% of people)
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
